import type { MapViewState } from '@/types';

export const MAP_STYLE = 'https://basemaps.cartocdn.com/gl/dark-matter-gl-style/style.json';

export const INITIAL_VIEW_STATE: MapViewState = {
  longitude: 105,
  latitude: 25,
  zoom: 2.2,
  pitch: 0,
  bearing: 0,
};

/** Threat level → color mapping */
export const THREAT_COLORS: Record<string, [number, number, number, number]> = {
  critical: [239, 68, 68, 220],   // red
  high: [249, 115, 22, 200],      // orange
  medium: [234, 179, 8, 180],     // yellow
  low: [34, 197, 94, 160],        // green
  info: [99, 102, 241, 140],      // indigo
};

/** Event type → color mapping */
export const EVENT_COLORS: Record<string, [number, number, number]> = {
  conflict: [220, 38, 38],
  protest: [168, 85, 247],
  earthquake: [245, 158, 11],
  wildfire: [239, 68, 68],
  military: [99, 102, 241],
  cyber: [16, 185, 129],
  economic: [59, 130, 246],
  diplomatic: [6, 182, 212],
  disaster: [249, 115, 22],
  terrorism: [190, 18, 60],
  health: [236, 72, 153],
  infrastructure: [148, 163, 184],
  news: [100, 116, 139],
};

/** Magnitude → radius (earthquake visualization) */
export function magnitudeToRadius(mag: number): number {
  return Math.max(4, Math.pow(2, mag) * 0.8);
}

/** Magnitude → color (earthquake visualization) */
export function magnitudeToColor(mag: number): [number, number, number] {
  if (mag >= 7) return [239, 68, 68];    // red
  if (mag >= 6) return [249, 115, 22];   // orange
  if (mag >= 5) return [234, 179, 8];    // yellow
  return [34, 197, 94];                   // green
}

// ── New layer colors ──────────────────────────────────────────────────────

export const WAR_ZONE_COLOR: [number, number, number, number] = [220, 38, 38, 50];
export const WAR_ZONE_STROKE: [number, number, number, number] = [239, 68, 68, 180];

export const AIRCRAFT_COLOR: [number, number, number, number] = [6, 182, 212, 220];
export const MILITARY_AIRCRAFT_COLOR: [number, number, number, number] = [99, 102, 241, 220];

export const SHIP_COLOR: [number, number, number, number] = [56, 189, 248, 200];

export const NAVAL_COLOR: [number, number, number, number] = [168, 85, 247, 220];
export const NAVAL_STROKE: [number, number, number, number] = [216, 180, 254, 180];

export const FIGHTER_COLOR: [number, number, number, number] = [251, 113, 133, 230];

export const POWER_PLANT_COLORS: Record<string, [number, number, number, number]> = {
  nuclear:    [251, 191, 36, 230],
  coal:       [120, 113, 108, 200],
  gas:        [52, 211, 153, 200],
  hydro:      [56, 189, 248, 200],
  wind:       [34, 197, 94, 180],
  solar:      [250, 204, 21, 180],
  oil:        [194, 65, 12, 200],
  geothermal: [217, 70, 239, 180],
};

export const DRONE_ZONE_COLOR: [number, number, number, number] = [249, 115, 22, 45];
export const DRONE_ZONE_STROKE: [number, number, number, number] = [251, 146, 60, 180];

export const NUCLEAR_ASSET_COLOR: [number, number, number, number] = [252, 211, 77, 220];

export const CABLE_COLORS: Record<string, [number, number, number, number]> = {
  active:    [99, 102, 241, 150],
  disrupted: [249, 115, 22, 200],
  cut:       [239, 68, 68, 220],
};

// Satellite tile overlay
// If VITE_MAPBOX_TOKEN is set → Mapbox Satellite (fast, high-res @2x retina)
// Otherwise → Esri World Imagery (free fallback)
const _mapboxToken = import.meta.env.VITE_MAPBOX_TOKEN as string | undefined;
export const SATELLITE_TILES: string[] = _mapboxToken
  ? [`https://api.mapbox.com/v4/mapbox.satellite/{z}/{x}/{y}@2x.jpg90?access_token=${_mapboxToken}`]
  : ['https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'];
export const HAS_MAPBOX = Boolean(_mapboxToken);

// NASA GIBS VIIRS Black Marble — night lights composite (free, no key)
// Source: NASA Earth Observing System Data and Information System (EOSDIS)
export const NIGHT_LIGHTS_TILES = [
  'https://gibs.earthdata.nasa.gov/wmts/epsg3857/best/VIIRS_Black_Marble/default/2016-01-01/500m/{z}/{y}/{x}.jpg',
];

// NASA GIBS MODIS Terra — recent true-color satellite (free, no key)
// Data is ~3 days behind current date
function gibsDate(): string {
  const d = new Date();
  d.setDate(d.getDate() - 3);
  return d.toISOString().slice(0, 10);
}
export const GIBS_RECENT_TILES = [
  `https://gibs.earthdata.nasa.gov/wmts/epsg3857/best/MODIS_Terra_CorrectedReflectance_TrueColor/default/${gibsDate()}/GoogleMapsCompatible_Level9/{z}/{y}/{x}.jpg`,
];

// ---------------------------------------------------------------------------
// Globe Basemaps — God's Eye 3D 글로브용 이미저리 레지스트리
//
// 전부 키 불필요 + 상용 사용 가능한 소스만 사용한다.
//   · NASA GIBS (EOSDIS) — U.S. 정부 저작물, 퍼블릭 도메인
//   · CARTO Dark Matter — 기존 택티컬 베이스맵 유지
// NC(비상업) 라이선스 데이터는 의도적으로 제외.
// ---------------------------------------------------------------------------

export type GlobeBasemapId = 'tactical' | 'satellite' | 'live' | 'night';

export interface GlobeBasemap {
  id: GlobeBasemapId;
  label: string;
  /** HUD/스위처에 쓰는 짧은 코드 */
  code: string;
  url: string;
  maxZoom: number;
  attribution: string;
  /** 지구 표면이 밝은 이미저리인가 — 마커 대비 조정용 */
  bright: boolean;
}

/** GIBS 는 당일 산출물이 늦게 올라오므로 UTC 기준 하루 전 날짜를 쓴다. */
function gibsLiveDate(): string {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - 1);
  return d.toISOString().slice(0, 10);
}

export const GLOBE_BASEMAPS: GlobeBasemap[] = [
  {
    id: 'tactical',
    label: 'Tactical Vector',
    code: 'TAC',
    url: 'https://basemaps.cartocdn.com/dark_all/{z}/{x}/{y}.png',
    maxZoom: 18,
    attribution: '© CARTO · © OpenStreetMap contributors',
    bright: false,
  },
  {
    id: 'satellite',
    label: 'Blue Marble Relief',
    code: 'BMR',
    url: 'https://gibs.earthdata.nasa.gov/wmts/epsg3857/best/BlueMarble_ShadedRelief_Bathymetry/default/default/GoogleMapsCompatible_Level8/{z}/{y}/{x}.jpeg',
    maxZoom: 8,
    attribution: 'NASA EOSDIS GIBS · Blue Marble',
    bright: true,
  },
  {
    id: 'live',
    label: 'VIIRS Live True Color',
    code: 'LIVE',
    url: `https://gibs.earthdata.nasa.gov/wmts/epsg3857/best/VIIRS_SNPP_CorrectedReflectance_TrueColor/default/${gibsLiveDate()}/GoogleMapsCompatible_Level9/{z}/{y}/{x}.jpg`,
    maxZoom: 9,
    attribution: `NASA EOSDIS GIBS · VIIRS/SNPP ${gibsLiveDate()}`,
    bright: true,
  },
  {
    id: 'night',
    label: 'Black Marble Night',
    code: 'NGT',
    url: 'https://gibs.earthdata.nasa.gov/wmts/epsg3857/best/VIIRS_Black_Marble/default/default/GoogleMapsCompatible_Level8/{z}/{y}/{x}.png',
    maxZoom: 8,
    attribution: 'NASA EOSDIS GIBS · Black Marble',
    bright: false,
  },
];

export const DEFAULT_GLOBE_BASEMAP: GlobeBasemapId = 'satellite';

export function getGlobeBasemap(id: GlobeBasemapId): GlobeBasemap {
  return GLOBE_BASEMAPS.find((b) => b.id === id) ?? GLOBE_BASEMAPS[1];
}
